<script setup lang="ts">
import { computed } from "vue";
import { kdm } from '@/store/kdm';
import KDMCell from "@/kdm/KDMCell.vue";
import KDMViewAxisToggle from "@/kdm/KDMViewAxisToggle.vue";
import { Axis } from "#/KrrNode";

/**
 * Pivoted datasheet: looks at the KDM cube from the TOC or QPI axis instead of
 * the Stakeholder axis. The active tab (props.tab) is the fixed page of that
 * axis; Stakeholders are ALWAYS the columns; the remaining structural axis (QPI
 * for a TOC view, TOC for a QPI view) provides the rows.
 *
 * Every cell still maps to the canonical (QPI, TOC, SH) triple fed to KDMCell,
 * so all behaviour — editability (gated on the stakeholder assignment), sliders,
 * comments, AI, locking — is identical to the stakeholder view, for free.
 */
const props = defineProps<{
  viewAxis: 'toc' | 'qpi',
  tab: Axis,
}>();

// Stakeholders are always the columns in a pivoted view.
const columns = computed<Axis[]>(() => kdm.value?.stake_holders || []);

// Rows are the remaining structural axis: QPI for a TOC view, TOC for a QPI view.
const rows = computed<Axis[]>(() =>
  props.viewAxis === 'toc' ? (kdm.value?.rows || []) : (kdm.value?.columns || [])
);

// Map (row item, stakeholder column) to the canonical KDMCell axis triple.
function cellAxes(rowItem: Axis, sh: Axis): { rid: string, cid: string, tid: string } {
  if (props.viewAxis === 'toc') {
    // Page = TOC (props.tab) · rows = QPI (rowItem) · cols = SH.
    return { rid: rowItem.id, cid: props.tab.id, tid: sh.id };
  }
  // viewAxis === 'qpi': page = QPI (props.tab) · rows = TOC (rowItem) · cols = SH.
  return { rid: props.tab.id, cid: rowItem.id, tid: sh.id };
}
</script>

<template>
  <table class="epb-matrix matrix-theme-evaluation" v-if="kdm">
    <thead>
      <tr>
        <th class="corner-th"><KDMViewAxisToggle/></th>
        <th v-for="sh in columns" :key="sh.id">
          <div class="th-content">
            <a :href="sh.href" :class="sh.moderation_class">{{ sh.title }}</a>
          </div>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in rows" :key="row.uuid || row.id">
        <td
          class="sticky-column"
          :title="row.is_in_kik === false ? 'Indicator/Argument no longer contextualized in the KIK' : ''"
        >
          <div class="row-header">
            <a :href="row.href" :class="[row.moderation_class, row.is_in_kik === false ? 'fw-bold' : '']">{{ row.title }}</a>
          </div>
        </td>
        <template v-for="sh in columns" :key="`${tab.id}-${row.id}-${sh.id}`">
          <KDMCell v-bind="cellAxes(row, sh)" />
        </template>
      </tr>
      <tr v-if="rows.length === 0 || columns.length === 0">
        <td class="empty-pivot" :colspan="columns.length + 1">
          <i class="bi bi-inbox me-2"></i>No data on this axis.
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
.epb-matrix {
  margin-top: 1rem;
  table-layout: fixed;
}

/* Sticky left column (row header), mirrors KDMTable. */
.sticky-column {
  background-color: #e9ecef;
  color: #212529;
  text-align: right;
  width: 150px;
}
.sticky-column a {
  color: #212529;
  font-weight: 600;
  text-decoration: none;
}
tr:hover .sticky-column a {
  color: #212529;
  text-decoration: underline dotted;
}
tr:nth-child(even) td:not(.sticky-column) {
  background-color: whitesmoke;
}
.row-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

/* Column headers (stakeholders), mirrors KDMTableHeaderRow. */
thead th {
  position: sticky;
  top: 0;
  background-color: #6c757d;
  color: #fff;
  z-index: 2;
  text-align: left;
  padding: 10px;
  border: 1px solid #ccc;
  border-bottom: 2px solid #ccc;
  font-size: 14px;
  width: var(--kdm-column-width, 150px);
  min-width: var(--kdm-column-width, 150px);
  max-width: var(--kdm-column-width, 150px);
  vertical-align: top;
}
thead th.corner-th {
  width: 150px;
  min-width: 150px;
  max-width: 150px;
}
thead th a {
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  display: block;
}
thead th:hover a {
  text-decoration: underline dotted;
}
.th-content {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}
.empty-pivot {
  text-align: center;
  padding: 1.5rem;
  color: #6c757d;
  font-style: italic;
}
</style>
